import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import {LoadingLine} from '../loadingLine/renderLoadingPanel.react';
import meQuery from '../queries/MeQuery';
import ChannelAddStep1 from '../channels/ChannelAddStep1.react';
import ChannelOptionEdit from '../channels/ChannelOptionEdit.react';
import ChannelAddStep2 from '../channels/ChannelAddStep2.react';
import ChannelAddStep3 from '../channels/ChannelAddStep3.react';
import ChannelAddStep4 from '../channels/ChannelAddStep4.react';
import ChannelAddStep5 from '../channels/ChannelAddStep5.react';
import LastOrganisationId from '../app/LastOrganisationId';
import {getComponentForChannelCreate} from '../channels/getUrlForChannelCreate';
import {safeJSONparse} from "../../common/util/safeJSONparse";
import { ChannelDef, Scenario } from '../../common/types/types';

const STEP_1 = 1;
const STEP_2 = 2;
const STEP_3 = 3;
const STEP_4 = 4;
const STEP_5 = 5;

interface ScenarioChannelConnectProps {
 cancel?: (...args: unknown[]) => unknown | boolean;
 channelDef: ChannelDef;
 chooserAlternative?: boolean;
 connectCallback?: (...args: unknown[]) => unknown;
 organisationId?: string;
 scenario?: Scenario;
}

export default class ScenarioChannelConnect extends React.Component<ScenarioChannelConnectProps> {
    static propTypes = {
        cancel: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
        channelDef: PropTypes.object.isRequired,
        chooserAlternative: PropTypes.bool,
        connectCallback: PropTypes.func,
        organisationId: PropTypes.string,
        scenario: PropTypes.object,
    };

    static defaultProps = {
        chooserAlternative: false,
        organisationId: LastOrganisationId.get()
    };

    constructor(props) {
        super(props);

        this.queryConfig = {
            queries: meQuery,
            name: 'User',
            params: {
                channelDefId: props.channelDef.realId,
                organisationId: props.organisationId,
                scenarioId: props.scenario ? props.scenario.realId : null
            }
        };

        this.state = {
            step: STEP_1
        };
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (JSON.stringify(nextState) !== JSON.stringify(this.state)) {
            return true;
        }

        if (JSON.stringify(nextProps) !== JSON.stringify(this.props)) {
            return true;
        }

        return false;
    }

    renderStep1() {
        LoadingLine.start();

        return (<Relay.Renderer
            Container={ChannelAddStep1}
            environment={Relay.Store}
            queryConfig={this.queryConfig}
            render={(state) => {
                if (state.done && this.step !== this.state.step) {
                    LoadingLine.stop();
                    this.step = this.state.configurationStep;
                    return (<ChannelAddStep1 {...state.props} cancel={this.props.cancel ? () => { this.props.cancel(); } : false} createCallback={(channel) => {this.connectChannelStep1Callback(channel);}} wrapper />);
                }
            }}
        />);
    }

    connectChannelStep1Callback(channel) {
        let nextComponent = getComponentForChannelCreate(channel.channelDef);
        let nextStep;

        this.queryConfig.params.channelId = channel.realId;

        switch (nextComponent) {
            case ChannelOptionEdit:
                nextStep = STEP_2;
                break;
            case ChannelAddStep2:
                nextStep = STEP_2;
                break;
            case ChannelAddStep3:
                nextStep = STEP_3;
                break;
            case ChannelAddStep4:
                nextStep = STEP_4;
                break;
            case ChannelAddStep5:
                nextStep = STEP_5;
                break;
        }

        this.setState({
            step: nextStep,
        });
    }

    renderStep2() {
        LoadingLine.start();

        return (<Relay.Renderer
            Container={ChannelAddStep2}
            environment={Relay.Store}
            queryConfig={this.queryConfig}
            render={(state) => {
                if (state.done) {
                    LoadingLine.stop();
                    return (<ChannelAddStep2 {...state.props} cancel={this.props.cancel ? () => { this.props.cancel(); } : false} connect createCallback={(channel) => {this.connectChannelStep2Callback(channel);}} loadingStopFn={() => {LoadingLine.stop();}} step={2} wrapper />);
                }
            }}
        />);
    }

    connectChannelStep2Callback(channel) {
        let metadata = safeJSONparse(channel.channelDef.metadata);

        if (!channel.activated && metadata.hasOptionsAfterGrant) {
            this.setState({
                step: STEP_3
            });
        } else {
            return this.connectChannelStep3Callback(channel);
        }
    }

    renderStep3() {
        return (<Relay.Renderer
            Container={ChannelAddStep3}
            environment={Relay.Store}
            queryConfig={this.queryConfig}
            render={(state) => {
                if (state.done) {
                    LoadingLine.stop();
                    return (<ChannelAddStep3 {...state.props} cancel={this.props.cancel ? () => { this.props.cancel(); } : false} createCallback={(channel) => {this.connectChannelStep3Callback(channel);}} loadingStopFn={() => {LoadingLine.stop();}} wrapper />);
                }
            }}
        />);
    }

    connectChannelStep3Callback(channel) {
        let metadata = safeJSONparse(channel.channelDef.metadata);

        if (!channel.activated && metadata.hasGrant) {
            this.setState({
                step: STEP_4
            });
        }
        else {
            return this.connectChannelStep4Callback(channel);
        }
    }

    renderStep4() {
        LoadingLine.start();

        return (<Relay.Renderer
            Container={ChannelAddStep4}
            environment={Relay.Store}
            queryConfig={this.queryConfig}
            render={(state) => {
                if (state.done) {
                    LoadingLine.stop();
                    return (<ChannelAddStep4 {...state.props} cancel={this.props.cancel ? () => { this.props.cancel(); } : false} createCallback={(channel) => {this.connectChannelStep4Callback(channel);}} loadingStopFn={() => {LoadingLine.stop();}} step={4} wrapper />);
                }
            }}
        />);
    }

    connectChannelStep4Callback(channel) {
        if (!channel.activated) {
            this.setState({
                step: STEP_5
            });
        }
        else {
            return this.connectChannelStep5Callback(channel);
        }
    }

    renderStep5() {
        LoadingLine.start();

        return (<Relay.Renderer
            Container={ChannelAddStep5}
            environment={Relay.Store}
            queryConfig={this.queryConfig}
            render={(state) => {
                if (state.done) {
                    LoadingLine.stop();
                    return (<ChannelAddStep5 {...state.props} cancel={this.props.cancel ? () => { this.props.cancel(); } : false} createCallback={(channel) => {this.connectChannelStep5Callback(channel);}} loadingStopFn={() => {LoadingLine.stop();}} wrapper />);
                }
            }}
        />);
    }

    connectChannelStep5Callback(channel) {
        this.props.connectCallback(channel);
    }

    render() {
        let content = null;

        switch (this.state.step) {
            case STEP_1:
                content = this.renderStep1();
                break;
            case STEP_2:
                content = this.renderStep2();
                break;
            case STEP_3:
                content = this.renderStep3();
                break;
            case STEP_4:
                content = this.renderStep4();
                break;
            case STEP_5:
                content = this.renderStep5();
                break;
            }

        return (
            <div>
                <LoadingLine />
                {content}
            </div>
        );
    }
}
